<script setup lang="ts">
/**
 * Wave 1 — Property access modals (Agent C).
 *
 * Mount this ONCE near the bottom of `pages/properties/[id].vue`.
 * It listens to the page-level AccessGate and renders the right
 * modal for the current `(level, action)` tuple.
 *
 * Wave 2 wires sign-in / sign-up nav and number-change flows.
 *
 * Sign-in / sign-up CTAs route to existing onboarding pages by
 * default — override via the optional handlers below.
 */

import ModalGuestGeneric from './ModalGuestGeneric.vue';
import ModalGuestCommission from './ModalGuestCommission.vue';
import ModalGuestDocument from './ModalGuestDocument.vue';
import ModalVerifyAvailability from './ModalVerifyAvailability.vue';
import ModalVerifyOfferPDF from './ModalVerifyOfferPDF.vue';
import ModalVerifyDevContact from './ModalVerifyDevContact.vue';
import OtpEntryModal from './OtpEntryModal.vue';

const {
  signupHref,
  signinHref,
  // The portal renders the whole profile (incl. personal info) at /profile.
  // /profile/personal-info doesn't exist and would 404.
  changeNumberHref = '/profile',
} = defineProps<{
  signupHref?: string;
  signinHref?: string;
  changeNumberHref?: string;
}>();

const { t } = useI18n();

const router = useRouter();
const { level, open, close, resumeAfterVerify } = useAccessGate();
const authStore = useAuthStore();
// Build redirect-aware /auth/{register|login} URLs so the user lands back on
// the gated property page after completing signup/signin. Callers may still
// override via the `signupHref` / `signinHref` props — the defaults match the
// real auth pages (NOT the legacy `/onboarding/*` routes that 404'd).
const { buildRedirectUrl } = useRedirectAfterAuth();
const resolvedSignupHref = computed(() => signupHref ?? buildRedirectUrl('/auth/register'));
const resolvedSigninHref = computed(() => signinHref ?? buildRedirectUrl('/auth/login'));

const otpOpen = ref(false);

const userPhoneMasked = computed(() => {
  const phone = authStore.user?.phone ?? '';
  if (!phone) return '+971 50 ••• ••24';
  const trimmed = phone.replace(/\s+/g, '');
  if (trimmed.length < 7) return phone;
  const head = trimmed.slice(0, 4);
  const tail = trimmed.slice(-2);
  const middle = '•'.repeat(Math.max(3, trimmed.length - 6));
  return `${head} ${middle} ${tail}`;
});

const navigateExternal = (href: string) => router.push(href);

const handleSignup = () => {
  close();
  navigateExternal(resolvedSignupHref.value);
};
const handleSignin = () => {
  close();
  navigateExternal(resolvedSigninHref.value);
};
const handleChangeNumber = () => {
  close();
  otpOpen.value = false;
  navigateExternal(changeNumberHref);
};

const uiStore = useUIStore();

const startVerify = () => {
  // /api/auth/phone/send returns 400 when the authenticated user has no
  // phone on file — the OTP modal would just bounce. Tell the user *why*
  // we're redirecting before sending them to /profile to add a number,
  // otherwise the jump is jarring and looks like a broken flow.
  if (!authStore.user?.phone) {
    uiStore.showSnackBanner('error', t('property.access.accessGateModalsHost.phoneRequiredError'));
    close();
    navigateExternal(changeNumberHref);
    return;
  }
  otpOpen.value = true;
};

const onVerified = async () => {
  otpOpen.value = false;
  await resumeAfterVerify();
};

const onOtpClose = () => {
  otpOpen.value = false;
};

// Modal selection — `gate(action)` semantics:
//   guest      + commission   → ModalGuestCommission
//   guest      + document     → ModalGuestDocument
//   guest      + *            → ModalGuestGeneric
//   registered + sales-offer  → ModalVerifyOfferPDF
//   registered + dev-contact  → ModalVerifyDevContact
//   registered + *            → ModalVerifyAvailability
const showGuestGeneric = computed(
  () =>
    open.value !== null &&
    level.value === 'guest' &&
    open.value.action !== 'commission' &&
    open.value.action !== 'document',
);
const showGuestCommission = computed(
  () => open.value !== null && level.value === 'guest' && open.value.action === 'commission',
);
const showGuestDocument = computed(
  () => open.value !== null && level.value === 'guest' && open.value.action === 'document',
);
const showVerifyAvailability = computed(
  () =>
    open.value !== null &&
    level.value === 'registered' &&
    open.value.action !== 'sales-offer' &&
    open.value.action !== 'dev-contact',
);
const showVerifyOffer = computed(
  () => open.value !== null && level.value === 'registered' && open.value.action === 'sales-offer',
);
const showVerifyDevContact = computed(
  () => open.value !== null && level.value === 'registered' && open.value.action === 'dev-contact',
);
</script>

<template>
  <ModalGuestGeneric
    v-if="showGuestGeneric"
    @close="close"
    @signup-click="handleSignup"
    @signin-click="handleSignin"
  />
  <ModalGuestCommission
    v-else-if="showGuestCommission"
    :project-title="open?.ctx?.projectTitle"
    :teaser-aed="open?.ctx?.teaserAed"
    @close="close"
    @signup-click="handleSignup"
    @signin-click="handleSignin"
  />
  <ModalGuestDocument
    v-else-if="showGuestDocument"
    :document-name="open?.ctx?.documentName"
    :document-meta="open?.ctx?.documentMeta"
    @close="close"
    @signup-click="handleSignup"
    @signin-click="handleSignin"
  />

  <template v-if="!otpOpen">
    <ModalVerifyAvailability
      v-if="showVerifyAvailability"
      :user-phone-masked="userPhoneMasked"
      @close="close"
      @verify-start="startVerify"
      @change-number="handleChangeNumber"
    />
    <ModalVerifyOfferPDF
      v-else-if="showVerifyOffer"
      :unit-label="open?.ctx?.unitLabel ?? t('property.shell.selectedUnitFallback')"
      :user-phone-masked="userPhoneMasked"
      @close="close"
      @verify-start="startVerify"
      @change-number="handleChangeNumber"
    />
    <ModalVerifyDevContact
      v-else-if="showVerifyDevContact"
      :developer-name="open?.ctx?.developerName ?? t('property.shell.theDeveloper')"
      :manager-name="open?.ctx?.managerName ?? t('property.access.shared.accountManager')"
      :manager-role="open?.ctx?.managerRole ?? t('property.access.shared.accountManager')"
      :user-phone-masked="userPhoneMasked"
      @close="close"
      @verify-start="startVerify"
      @change-number="handleChangeNumber"
    />
  </template>

  <OtpEntryModal
    v-if="otpOpen"
    :user-phone-masked="userPhoneMasked"
    @close="onOtpClose"
    @verified="onVerified"
    @change-number="handleChangeNumber"
  />
</template>
